/**
 * SplatViewer with simplified XR support
 */

import { useEffect, useRef, useState } from 'react'
import { SplatRenderer } from './splatv-renderer'
import { useXRSimple } from '@/hooks/useXRSimple'
import type { CameraState } from '@/types/camera'

interface SplatViewerXRProps {
  modelUrl: string
  defaultCamera?: CameraState
  onLoad?: () => void
  onError?: (error: Error) => void
}

export function SplatViewerXR({
  modelUrl,
  defaultCamera,
  onLoad,
  onError
}: SplatViewerXRProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const rendererRef = useRef<SplatRenderer | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  const xr = useXRSimple({
    onSessionStart: () => {
      console.log('XR session started')
    },
    onSessionEnd: () => {
      console.log('XR session ended')
    }
  })
  
  // Initialize renderer
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    
    const renderer = new SplatRenderer(canvas, {
      controls: true,
      quality: 'high',
      defaultCamera
    })
    
    rendererRef.current = renderer
    
    // Load model
    renderer.loadModel(modelUrl)
    
    // Handle events
    renderer.on('load', () => {
      console.log('Model loaded successfully')
      setIsLoading(false)
      
      // Set the splatv module for XR
      const splatvModule = renderer.getSplatvModule()
      console.log('SplatV module:', splatvModule)
      if (splatvModule) {
        xr.setRenderer(splatvModule)
        console.log('Renderer set for XR')
      }
      
      onLoad?.()
    })
    
    renderer.on('error', (err: any) => {
      setIsLoading(false)
      const errorMessage = err?.message || 'Unknown error'
      setError(errorMessage)
      if (onError && err instanceof Error) {
        onError(err)
      }
    })
    
    // Check XR support - properly handle async
    console.log('Checking XR support...')
    xr.checkSupport().then(() => {
      console.log('XR support check complete:', {
        arSupported: xr.state.arSupported,
        vrSupported: xr.state.vrSupported
      })
    }).catch(err => {
      console.error('Failed to check XR support:', err)
    })
    
    return () => {
      renderer.dispose()
    }
  }, [modelUrl])
  
  // Debug XR state
  useEffect(() => {
    console.log('XR Button conditions:', {
      isLoading,
      error,
      isActive: xr.state.isActive,
      arSupported: xr.state.arSupported,
      vrSupported: xr.state.vrSupported
    })
  }, [isLoading, error, xr.state])
  
  const handleEnterAR = () => {
    console.log('handleEnterAR clicked')
    xr.enterAR(defaultCamera)
  }
  
  const handleEnterVR = () => {
    console.log('handleEnterVR clicked')
    xr.enterVR(defaultCamera)
  }
  
  return (
    <div className="relative w-full h-full bg-gray-900">
      {/* Canvas */}
      <canvas
        ref={canvasRef}
        className="w-full h-full"
        style={{ touchAction: 'none' }}
      />
      
      {/* Test Button with inline styles - should ALWAYS be visible */}
      <div style={{ 
        position: 'fixed', 
        bottom: '20px', 
        left: '20px', 
        zIndex: 99999,
        pointerEvents: 'all'
      }}>
        <button 
          onClick={() => {
            alert('Button clicked! Trying AR...')
            handleEnterAR()
          }}
          style={{
            padding: '20px',
            fontSize: '20px',
            backgroundColor: '#ff0000',
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            cursor: 'pointer',
            pointerEvents: 'all'
          }}
        >
          FORCE AR TEST
        </button>
      </div>
      
      {/* Simple XR buttons with inline styles - ALWAYS VISIBLE */}
      <div style={{ 
        position: 'fixed', 
        bottom: '20px', 
        right: '20px', 
        zIndex: 99999,
        display: 'flex',
        gap: '10px',
        pointerEvents: 'all'
      }}>
        <button 
          onClick={() => {
            alert('AR button clicked!')
            if (xr.state.arSupported) {
              handleEnterAR()
            } else {
              alert('AR not supported on this device')
            }
          }}
          style={{
            padding: '15px 30px',
            fontSize: '18px',
            backgroundColor: xr.state.arSupported ? '#9333ea' : '#666666',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            pointerEvents: 'all'
          }}
        >
          Enter AR {xr.state.arSupported ? '✓' : '✗'}
        </button>
        
        <button 
          onClick={() => {
            alert('VR button clicked!')
            if (xr.state.vrSupported) {
              handleEnterVR()
            } else {
              alert('VR not supported on this device')
            }
          }}
          style={{
            padding: '15px 30px',
            fontSize: '18px',
            backgroundColor: xr.state.vrSupported ? '#4f46e5' : '#666666',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            pointerEvents: 'all'
          }}
        >
          Enter VR {xr.state.vrSupported ? '✓' : '✗'}
        </button>
      </div>
      
      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50">
          <div className="text-white">Loading model...</div>
        </div>
      )}
      
      {/* Error overlay */}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50">
          <div className="text-red-500">Error: {error}</div>
        </div>
      )}
      
      {/* Debug Info with inline styles */}
      <div style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        backgroundColor: 'rgba(0,0,0,0.8)',
        color: 'white',
        padding: '10px',
        borderRadius: '5px',
        fontSize: '12px',
        zIndex: 9998
      }}>
        <div>Loading: {isLoading ? 'YES' : 'NO'}</div>
        <div>Error: {error || 'none'}</div>
        <div>navigator.xr: {navigator.xr ? 'YES' : 'NO'}</div>
        <div>AR Support: {xr.state.arSupported ? 'YES' : 'NO'}</div>
        <div>VR Support: {xr.state.vrSupported ? 'YES' : 'NO'}</div>
        <div>XR Active: {xr.state.isActive ? 'YES' : 'NO'}</div>
      </div>
    </div>
  )
}